import type { AccountIntent } from './accountStateMachine.js'
import type { RuntimePolicy } from './runtimePolicy.js'

export type AuthModalMode =
  | 'email'
  | 'username'
  | 'checking-human'
  | 'checking-human-failed'
  | 'email-sent'
  | 'checkout-start'
  | 'sync-subscription-threshold'
  | 'checkout-activating'
  | 'checkout-landing'
  | 'checkout-timeout'
  | 'sync-cancel-subscription'
  | 'sync-cancelled'
  | 'sync-reactivate-from-deactivated'
  | 'sync-reactivate-subscription'
  | 'sync-reactivated'
  | 'local-found'
  | 'anonymous-login-gate'
  | 'deactivated-local-found'
  | 'protect-local-space-open-file'
  | 'protect-current-space-open-file'
  | 'account-delete-export'
  | 'account-delete-confirm'
  | 'sync-exit-unsafe'
  | 'local-memory-drive-recovery'
  | 'machine-consent'
  | 'machine-consent-expired'

export type ModalFamily =
  | 'none'
  | 'auth'
  | 'sync-activation'
  | 'subscription-control'
  | 'account-delete'
  | 'local-protection'

export type ModalCheckoutState =
  | 'none'
  | 'threshold'
  | 'options'
  | 'activating'
  | 'landing'
  | 'timeout'
  | 'cancel-pending'

export type ModalFactsInput = {
  runtimePolicy: RuntimePolicy
  activeMode?: AuthModalMode
  accountIdentityInFlight: boolean
  accountAirlockInFlight: boolean
  checkoutCancelPending: boolean
  checkoutAttempts: number
  otpAwaitingCode: boolean
  hasPendingOtpSendIntent: boolean
  turnstileVerificationInFlight: boolean
  accountDeleteExportReady: boolean
  accountDeleteInFlight: boolean
  syncTerminateInFlight: boolean
  localFoundActive: boolean
  deactivatedLocalFoundActive: boolean
  openFileGuardActive: boolean
  pendingAccountIntent?: AccountIntent
}

export type ModalFacts = {
  runtime: RuntimePolicy
  activeMode?: AuthModalMode
  activeFamily: ModalFamily
  isAccountActivity: boolean
  isCloseBlocked: boolean
  isAsyncBlocking: boolean
  checkout: {
    state: ModalCheckoutState
    attempts: number
  }
  otp: {
    awaitingCode: boolean
    hasPendingSend: boolean
    checkingHuman: boolean
  }
  accountDelete: {
    exportReady: boolean
    inFlight: boolean
  }
  protection: {
    localFoundActive: boolean
    deactivatedLocalFoundActive: boolean
    openFileGuardActive: boolean
  }
  pendingAccountIntent?: AccountIntent
}

export type ModalEscapeCloseInput = {
  isVisible: boolean
  hasActiveMode: boolean
  hasCloseControl: boolean
  isCloseControlVisible: boolean
  isCloseBlocked: boolean
  accountDeleteInFlight: boolean
  syncTerminateInFlight: boolean
}

const CLOSE_BLOCKED_MODES = new Set<AuthModalMode>([
  'username',
  'local-found',
  'local-memory-drive-recovery'
])

export function canCloseModalWithEscape(input: ModalEscapeCloseInput): boolean {
  return input.isVisible
    && input.hasActiveMode
    && input.hasCloseControl
    && input.isCloseControlVisible
    && !input.isCloseBlocked
    && !input.accountDeleteInFlight
    && !input.syncTerminateInFlight
}

export function selectModalFacts(input: ModalFactsInput): ModalFacts {
  const activeFamily = selectModalFamily(input.activeMode)
  const checkoutState = selectModalCheckoutState(input.activeMode, input.checkoutCancelPending)
  const isAccountActivity = input.accountIdentityInFlight || input.accountAirlockInFlight
  const checkingHuman = input.activeMode === 'checking-human'
    || input.activeMode === 'checking-human-failed'
  const isAsyncBlocking = input.accountDeleteInFlight
    || input.syncTerminateInFlight
    || input.turnstileVerificationInFlight
    || input.activeMode === 'checkout-activating'

  return {
    runtime: input.runtimePolicy,
    activeMode: input.activeMode,
    activeFamily,
    isAccountActivity,
    isCloseBlocked: input.accountDeleteInFlight
      || input.syncTerminateInFlight
      || (input.activeMode ? CLOSE_BLOCKED_MODES.has(input.activeMode) : false),
    isAsyncBlocking,
    checkout: {
      state: checkoutState,
      attempts: input.checkoutAttempts
    },
    otp: {
      awaitingCode: input.otpAwaitingCode,
      hasPendingSend: input.hasPendingOtpSendIntent,
      checkingHuman
    },
    accountDelete: {
      exportReady: input.accountDeleteExportReady,
      inFlight: input.accountDeleteInFlight
    },
    protection: {
      localFoundActive: input.localFoundActive,
      deactivatedLocalFoundActive: input.deactivatedLocalFoundActive,
      openFileGuardActive: input.openFileGuardActive
    },
    pendingAccountIntent: input.pendingAccountIntent
  }
}

function selectModalFamily(mode?: AuthModalMode): ModalFamily {
  if (!mode) return 'none'
  if (
    mode === 'local-found'
    || mode === 'anonymous-login-gate'
    || mode === 'deactivated-local-found'
    || mode === 'protect-local-space-open-file'
    || mode === 'protect-current-space-open-file'
    || mode === 'sync-exit-unsafe'
    || mode === 'local-memory-drive-recovery'
  ) {
    return 'local-protection'
  }
  if (mode === 'account-delete-export' || mode === 'account-delete-confirm') return 'account-delete'
  if (
    mode === 'sync-cancel-subscription'
    || mode === 'sync-cancelled'
    || mode === 'sync-reactivate-subscription'
    || mode === 'sync-reactivated'
  ) {
    return 'subscription-control'
  }
  if (
    mode === 'sync-subscription-threshold'
    || mode === 'checkout-start'
    || mode === 'checkout-activating'
    || mode === 'checkout-landing'
    || mode === 'checkout-timeout'
    || mode === 'sync-reactivate-from-deactivated'
  ) {
    return 'sync-activation'
  }
  return 'auth'
}

function selectModalCheckoutState(
  mode: AuthModalMode | undefined,
  checkoutCancelPending: boolean
): ModalCheckoutState {
  if (checkoutCancelPending) return 'cancel-pending'
  if (mode === 'sync-subscription-threshold') return 'threshold'
  if (mode === 'checkout-start') return 'options'
  if (mode === 'checkout-activating') return 'activating'
  if (mode === 'checkout-landing') return 'landing'
  if (mode === 'checkout-timeout') return 'timeout'
  return 'none'
}
